import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import Icon from '../../forms/icon.component';
import { BasicConfig } from './interactive/basic-config.component';
import { Blendy, createBlendy } from 'blendy';
import { Node } from 'reactflow';
import { NodeData, Position } from '../../../pages/campaings/flows/types';
import { ContentConfig } from './interactive/content-config.component';
import { RulesConfig } from './interactive/rules-config.component';
import { AlertService } from '../../../services/alert.service';
import { AssetsServices } from '../../../services/assets.service';

interface InteractiveNodeFormProps {
  node: Node<NodeData>;
  onSave: (nodeId: string, data: NodeData, position: Position) => void;
}

const InteractiveNodeForm = forwardRef<HTMLFormElement, InteractiveNodeFormProps>(
  ({ node, onSave }, ref) => {
    const methods = useForm({ defaultValues: node.data, mode: 'onChange' });
    const [showBasic, setShowBasic] = useState(false);
    const [showContent, setShowContent] = useState(false);
    const [showRules, setShowRules] = useState(false);
    const blendy = useRef<Blendy | null>(null);

    const title = methods.watch('title');
    const type = methods.watch('interactive.type');
    const text = methods.watch('interactive.text.es');
    const body = methods.watch('interactive.buttons.body.es');
    const buttons = methods.watch('interactive.buttons.action.sections.rows') || [];
    const attempts = methods.watch('interactive.attempts_before_close.attempts');
    const attemptType = methods.watch('interactive.attempts_before_close.type');
    const attemptsClose = methods.watch('interactive.attempts_before_close.close.category');
    const attemptsMessage = methods.watch('interactive.attempts_before_close.message.es');

    const isBasicDone = !!(title && type);
    const isContentDone =
      type === 'TEXT'
        ? !!text && text.trim().length > 0
        : type === 'BUTTONS'
          ? !!body && buttons.length > 0 && buttons.every((b) => b.es)
          : false;
    const isRulesDone = !!attempts && !!attemptType && !!attemptsClose && !!attemptsMessage;
    const anyOpen = showBasic || showContent || showRules;
    const openBasic = () => {
      if (anyOpen) {
        AlertService.toastWarning('Por favor, cierra la configuración actual antes de abrir otra.');
        return;
      }
      setShowBasic(true);
      blendy.current?.toggle('nodeModal');
    };

    const openContent = () => {
      if (!isBasicDone) return;
      if (anyOpen) {
        AlertService.toastWarning('Por favor, cierra la configuración actual antes de abrir otra.');
        return;
      }
      setShowContent(true);
      blendy.current?.toggle('nodeContentModal');
    };

    const openRules = () => {
      if (!isContentDone) return;
      if (anyOpen) {
        AlertService.toastWarning('Por favor, cierra la configuración actual antes de abrir otra.');
        return;
      }
      setShowRules(true);
      blendy.current?.toggle('nodeRulesModal');
    };

    function cleanObject(obj: any): any {
      if (Array.isArray(obj)) {
        return obj.map(cleanObject).filter((val) => val !== undefined);
      }
      if (obj && typeof obj === 'object') {
        const entries = Object.entries(obj)
          .map(([k, v]) => [k, cleanObject(v)])
          .filter(([, v]) => {
            if (v === undefined) return false;
            if (v === '') return false;
            if (typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length === 0)
              return false;
            if (Array.isArray(v) && v.length === 0) return false;
            return true;
          });
        return Object.fromEntries(entries);
      }
      return obj;
    }

    const onSubmit = async (data: NodeData) => {
      if (!isBasicDone || !isContentDone || !isRulesDone)
        return AlertService.toastError('Faltan campos');
      const interactive = data.interactive;
      const pendingFile: File | undefined = interactive?.buttons?.image?.file as File | undefined;
      if (pendingFile) {
        const form = new FormData();
        form.append('file', pendingFile);
        try {
          const resp = await AssetsServices.uploadMedia(form);
          if (interactive?.buttons?.image) {
            interactive.buttons.image.es = resp.url;
          }
        } catch (err: any) {
          console.error('Error subiendo imagen:', err);
          return AlertService.toastError(err.error || 'No se pudo subir la imagen');
        }
      }

      if (interactive?.buttons?.image) {
        delete interactive.buttons.image.file;
      }
      if (Array.isArray(interactive?.triggers)) {
        const originalTriggers = interactive?.triggers;
        const transformed = originalTriggers?.map((t: any) => {
          const newTrigger: any = {
            after_minutes: t.after_minutes,
            text: { es: t.text?.es ?? '' }
          };
          if (t.close) {
            newTrigger.close = {
              category: t.close.category
            };
          }
          if (t.webhook) {
            newTrigger.webhook = {
              url_dev: t.webhook.url_dev,
              url_prod: t.webhook.url_prod
            };
          }
          return newTrigger;
        });
        if (interactive) {
          interactive.triggers = transformed;
        }
      }
      const clean = cleanObject(interactive);
      data.interactive = clean;
      onSave(node.id, data, node.position);
    };

    useEffect(() => {
      blendy.current = createBlendy({ animation: 'dynamic' });
    }, []);

    return (
      <FormProvider {...methods}>
        {/* Badge informativo */}
        <div
          className="d-flex align-items-center mb-5 badge bg-light-info"
          style={{ whiteSpace: 'normal' }}
        >
          <Icon icon="ki-information" size="fs-3" type="info" />
          <small className="text-info ms-2">
            Haz click en las tarjetas para configurar cada sección del nodo interactivo.
          </small>
        </div>

        <form ref={ref} onSubmit={methods.handleSubmit(onSubmit)}>
          {/* Tarjeta Basic Config */}
          <div className="mb-4">
            <div
              className={`card p-3 border-light cursor-pointer shadow-sm interactive-config ${
                isBasicDone ? 'bg-light-primary' : 'bg-light-danger'
              }`}
              data-blendy-from="nodeModal"
              onClick={openBasic}
            >
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="card-title mb-0">Información básica</h6>
                <div className="d-flex align-items-center">
                  <Icon
                    icon={
                      methods.watch('title') && methods.watch('interactive.type')
                        ? 'ki-verify'
                        : 'ki-information'
                    }
                    size="fs-3"
                    type={
                      methods.watch('title') && methods.watch('interactive.type')
                        ? 'primary'
                        : 'danger'
                    }
                  />
                  <small className="ms-2">
                    {methods.watch('title') && methods.watch('interactive.type')
                      ? null
                      : 'Falta configurar'}
                  </small>
                </div>
              </div>
            </div>
          </div>
          {/* Tarjeta Content Config */}
          <div className="mb-4">
            <div
              onClick={openContent}
              className={`card p-3 border-light shadow-sm interactive-config ${
                !isBasicDone
                  ? 'bg-light-danger'
                  : isContentDone
                    ? 'bg-light-primary cursor-pointer'
                    : 'bg-light-warning cursor-pointer'
              }`}
              style={{
                opacity: isBasicDone ? 1 : 0.5,
                pointerEvents: isBasicDone ? 'auto' : 'none'
              }}
              data-blendy-from="nodeContentModal"
            >
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="card-title mb-0">Contenido del mensaje</h6>
                <div className="d-flex align-items-center">
                  <Icon
                    icon={
                      !isBasicDone
                        ? 'ki-information'
                        : isContentDone
                          ? 'ki-verify'
                          : 'ki-information'
                    }
                    size="fs-3"
                    type={!isBasicDone ? 'danger' : isContentDone ? 'primary' : 'warning'}
                  />
                </div>
              </div>
            </div>
          </div>
          {/* Tarjeta Rules Config */}
          <div className="mb-4">
            <div
              onClick={openRules}
              className={`card p-3 border-light shadow-sm interactive-config ${
                !isContentDone
                  ? 'bg-light-danger'
                  : isRulesDone
                    ? 'bg-light-primary cursor-pointer'
                    : 'bg-light-warning cursor-pointer'
              }`}
              style={{
                opacity: isContentDone ? 1 : 0.6,
                pointerEvents: isContentDone ? 'auto' : 'none'
              }}
              data-blendy-from="nodeRulesModal"
            >
              <div className="d-flex align-items-center justify-content-between">
                <h6 className="card-title mb-0">Reglas de interacción</h6>
                <div className="d-flex align-items-center">
                  <Icon
                    icon={!isContentDone ? 'ki-information' : isRulesDone ? 'ki-verify' : 'ki-edit'}
                    size="fs-3"
                    type={!isContentDone ? 'danger' : isRulesDone ? 'primary' : 'warning'}
                  />
                </div>
              </div>
            </div>
          </div>
        </form>

        {/* Modal BasicConfig */}
        {showBasic && (
          <BasicConfig
            onClose={() => {
              blendy.current?.untoggle('nodeModal', () => setShowBasic(false));
            }}
          />
        )}
        {showContent && (
          <ContentConfig
            onClose={() => {
              blendy.current?.untoggle('nodeContentModal', () => setShowContent(false));
            }}
          />
        )}
        {showRules && (
          <RulesConfig
            onClose={() => blendy.current?.untoggle('nodeRulesModal', () => setShowRules(false))}
          />
        )}
      </FormProvider>
    );
  }
);

export default InteractiveNodeForm;
